import { useLocation, useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { componentIds, genericActionsIds } from 'utilities/constants';
import { ModalButtonProps } from 'components/modal/modal.interfaces';
import { downloadPdf } from 'utilities/functions';
import { useGetGenericActions } from 'hooks/useGetGenericActions';
import Modal from 'components/modal/modalComponent';
import Thumbnail from 'assets/images/thumbnail.svg';
import {
  SharedMediaModalLocationState,
  SharedMediaModalProps,
} from '../interfaces/interfaces';
import ButtonComponent from 'components/button/buttonComponent';
import { ReactComponent as FileDownloadIcon } from 'assets/icons/file-download.svg';
import { useGetComponent } from 'hooks/useGetComponent';
import { appointmentRenderInfo } from 'app/my-appointments/interfaces';
import Loader from 'components/loaderComponent';
import useLoadModalAppointmentInfo from 'app/my-account/pages/my-media-library/hooks/useLoadModalAppointmentInfo';
import { ReactComponent as Eye } from 'assets/icons/pwEyeShow.svg';
import { MY_APPOINTMENTS_DETAILS } from 'utilities/routes';
import moment from 'moment-timezone';

export const SharedMediaModal: React.FC<SharedMediaModalProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const { data: allLocales, loading: allLocalesLoading } = useGetComponent({
    locale: 'en',
    componentId: componentIds.MY_PATIENTS_MODALS,
  });
  const locale = allLocales?.mediaModal;

  const {
    mediaDate,
    mediaSrc,
    providerName: providerNameByParam,
    appointmentType: appointmentTypeByParam,
    bodySite,
    appointmentCodexId,
    mediaTitle,
  } = (location.state as SharedMediaModalLocationState) || {};

  const handleAppointmentDetails = () => {
    if (!appointmentCodexId) {
      return null;
    }
    return navigate(`${MY_APPOINTMENTS_DETAILS}/${appointmentCodexId}`);
  };

  const {
    loading: appointmentDataLoading,
    appointmentTypeByState,
    providerNameByState,
  } = useLoadModalAppointmentInfo(
    appointmentCodexId,
    appointmentTypeByParam,
    providerNameByParam,
  );

  const providerName = providerNameByState ?? providerNameByParam;
  const appointmentType = appointmentTypeByState ?? appointmentTypeByParam;

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const { data: genericAction, loading: genericActionLoading } =
    useGetGenericActions({
      locale: 'en',
      genericActionId: [genericActionsIds.CLOSE, genericActionsIds.DOWNLOAD],
    });

  const handleClose = () => {
    navigate(-1);
  };

  const Buttons: ModalButtonProps[] = [
    {
      label: genericAction?.[genericActionsIds.CLOSE].close,
      onClick: handleClose,
      testID: 'close',
    },
  ];

  const handleOnDownload = async () => {
    if (!mediaSrc) return;
    downloadPdf(mediaSrc, bodySite || mediaTitle || locale.unknownSource, true);
  };

  const AppointmentIcon =
    appointmentType !== undefined &&
    appointmentRenderInfo[appointmentType]?.icon;

  if (genericActionLoading || allLocalesLoading) return null;

  return (
    <Modal
      isOpen={true}
      title={locale?.modalTitle}
      titleContainerCustomClass="desktop:px-[30px]"
      titleCustomClass="text-charcoal-gray text-2xl font-bold lower-case"
      buttons={Buttons}
      onClose={handleClose}
      contentPaddingX="grow px-0 desktop:px-[30px]"
      contentPaddingY="py-0 desktop:py-[30px]"
      disableMobileGrow
      modalContainerCustomClass="desktop:!rounded-bl-none"
      buttonsCustomClass="desktop:px-[30px] !rounded-bl-none"
      contentContainerDefaultBackground="bg-white"
      contentContainerDefaultBorder="border-b-2 border-base-content"
      leftModalComponent={
        <div className="hidden desktop:flex bg-neutral-500 overflow-hidden w-full min-h-[600px] bg-cover bg-center justify-center items-center">
          {isLoading && (
            <img
              className="w-full object-cover"
              src={Thumbnail}
              alt="Default Image"
            />
          )}
          <img
            className={`w-full object-contain ${isLoading ? 'hidden' : ''}`}
            src={mediaSrc}
            onLoad={handleImageLoad}
            alt="Scan result"
          />
        </div>
      }
    >
      <div className="flex flex-col grow">
        <div className="desktop:hidden flex bg-neutral-500 overflow-hidden w-full h-[350px] desktop:rounded-b-10 bg-cover bg-center justify-center items-center">
          {isLoading && (
            <img
              className="w-full object-cover"
              src={Thumbnail}
              alt="Default Image"
            />
          )}
          <img
            className={`w-full object-contain ${isLoading ? 'hidden' : ''}`}
            src={mediaSrc}
            onLoad={handleImageLoad}
            alt="Scan result"
          />
        </div>
        {appointmentDataLoading ? (
          <Loader />
        ) : (
          <div className="flex grow flex-col gap-1 desktop:gap-1.5 min-w-full w-max px-7 py-5 desktop:px-0 desktop:py-0">
            <div className="text-neutral-800 text-lg font-semibold">
              {locale?.inAppointmentShare}
            </div>
            <div className="text-med-gray-3 text-base font-normal leading-snug">
              {moment(mediaDate).format('MM/DD/YYYY hh:mm A z')}
            </div>
            <div className="py-2.5 justify-start items-center gap-2.5 text-charcoal-gray inline-flex">
              {AppointmentIcon && (
                <div className="justify-center items-center w-6 h-6 flex">
                  <AppointmentIcon className="fill-med-gray-3" />
                </div>
              )}
              <div className="grow shrink basis-0 text-neutral-800 text-base font-semibold leading-snug">
                {providerName}
              </div>
            </div>

            <div className="w-full flex flex-col gap-4">
              {appointmentCodexId && (
                <ButtonComponent
                  type="outlined"
                  Icon={Eye}
                  iconPosition={'left'}
                  testID={'appointmentDetails'}
                  onClick={handleAppointmentDetails}
                >
                  {locale.appointmentDetails}
                </ButtonComponent>
              )}
            </div>

            <div className="flex flex-row grow justify-around items-end" />
            <ButtonComponent
              type="outlined"
              testID={'download'}
              Icon={FileDownloadIcon}
              onClick={handleOnDownload}
              iconPosition={'left'}
            >
              Download
            </ButtonComponent>
          </div>
        )}
      </div>
    </Modal>
  );
};
